import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Button,
  SimpleGrid,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  Flex,
  Tooltip,
  Progress,
  Image
} from '@chakra-ui/react';
import { FiSearch, FiCalendar, FiMapPin, FiUsers, FiTrendingUp, FiAward } from 'react-icons/fi';
import { useDataStore } from '../../contexts/DataStoreContext';
import { LoadingSpinner } from '../Common/LoadingSpinner';

export const EventHistory = () => {
  const { getAllEvents, loading } = useDataStore();
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    applyFilters();
  }, [getAllEvents, searchTerm, yearFilter, statusFilter, loading.events]);

  const applyFilters = () => {
    if (loading.events) return;

    // Get events from DataStore and sort by date, most recent first
    const eventsData = getAllEvents().sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB - dateA;
    });

    let filtered = [...eventsData];

    // Status filter
    if (statusFilter) {
      if (statusFilter === 'completed') {
        filtered = filtered.filter(event => !event.isActive);
      } else if (statusFilter === 'active') {
        filtered = filtered.filter(event => event.isActive);
      }
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Year filter
    if (yearFilter) {
      filtered = filtered.filter(event => {
        const eventDate = event.date instanceof Date ? event.date : new Date(event.date);
        return eventDate.getFullYear().toString() === yearFilter;
      });
    }

    setFilteredEvents(filtered);
  };

  const getAvailableYears = () => {
    const years = getAllEvents().map(event => {
      const eventDate = event.date instanceof Date ? event.date : new Date(event.date);
      return eventDate.getFullYear();
    });
    return [...new Set(years)].sort((a, b) => b - a);
  };

  const getEventStats = (event) => {
    const totalFights = event.fights?.length || 0;
    const completedFights = event.fights?.filter(fight => fight.status === 'completed').length || 0;
    const totalVotes = event.fights?.reduce((sum, fight) => sum + (fight.voteCount || 0), 0) || 0;

    return {
      totalFights,
      completedFights,
      totalVotes,
      completionRate: totalFights > 0 ? (completedFights / totalFights) * 100 : 0
    };
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    onOpen();
  };

  const EventCard = ({ event }) => {
    const stats = getEventStats(event);
    const eventDate = event.date instanceof Date ? event.date : new Date(event.date);

    return (
      <Card
        cursor="pointer"
        _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
        transition="all 0.2s"
        onClick={() => handleEventClick(event)}
        overflow="hidden"
      >
        {/* Cover Image */}
        {event.coverImage && (
          <Box>
            <Image
              src={event.coverImage}
              alt={`${event.name} cover image`}
              width="100%"
              height="150px"
              objectFit="cover"
            />
          </Box>
        )}
        <CardHeader pb={2}>
          <VStack align="start" spacing={2}>
            <HStack justify="space-between" w="full">
              <Heading size="md" color="white" noOfLines={1}>
                {event.name}
              </Heading>
              <Badge colorScheme={event.isActive ? 'green' : 'gray'} size="sm">
                {event.isActive ? 'Active' : 'Completed'}
              </Badge>
            </HStack>

            <HStack color="gray.300" fontSize="sm">
              <Icon as={FiMapPin} />
              <Text noOfLines={1}>{event.location}</Text>
            </HStack>

            <HStack color="gray.400" fontSize="sm">
              <Icon as={FiCalendar} />
              <Text>
                {eventDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </HStack>
          </VStack>
        </CardHeader>

        <CardBody pt={2}>
          <SimpleGrid columns={2} spacing={4}>
            <Stat size="sm">
              <StatLabel color="gray.400">Fights</StatLabel>
              <StatNumber color="white" fontSize="lg">
                {stats.totalFights}
              </StatNumber>
            </Stat>

            <Stat size="sm">
              <StatLabel color="gray.400">Completed</StatLabel>
              <StatNumber color="white" fontSize="lg">
                {stats.completedFights}
              </StatNumber>
            </Stat>
          </SimpleGrid>

          {stats.totalFights > 0 && (
            <Box mt={3}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" color="gray.400">Progress</Text>
                <Text fontSize="xs" color="gray.400">
                  {stats.completionRate.toFixed(0)}%
                </Text>
              </HStack>
              <Progress
                value={stats.completionRate}
                colorScheme={stats.completionRate === 100 ? 'green' : 'blue'}
                size="sm"
                borderRadius="md"
              />
            </Box>
          )}
        </CardBody>
      </Card>
    );
  };

  const EventDetailsModal = ({ event }) => {
    if (!event) return null;

    const stats = getEventStats(event);
    const eventDate = event.date instanceof Date ? event.date : new Date(event.date);

    return (
      <VStack spacing={6} align="stretch">
        {/* Event Header */}
        <Box>
          <Heading size="lg" color="white" mb={2}>
            {event.name}
          </Heading>
          <VStack align="start" spacing={2}>
            <HStack color="gray.300">
              <Icon as={FiMapPin} />
              <Text>{event.location}</Text>
            </HStack>
            <HStack color="gray.300">
              <Icon as={FiCalendar} />
              <Text>
                {eventDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </HStack>
            {event.description && (
              <Text color="gray.300" mt={2}>
                {event.description}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Event Statistics */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Card>
            <CardBody textAlign="center">
              <Stat size="sm">
                <StatLabel color="white">Total Fights</StatLabel>
                <StatNumber color="white">{stats.totalFights}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center">
              <Stat size="sm">
                <StatLabel color="white">Completed</StatLabel>
                <StatNumber color="green.400">{stats.completedFights}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center">
              <Stat size="sm">
                <StatLabel color="white">Total Votes</StatLabel>
                <StatNumber color="blue.400">{stats.totalVotes}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center">
              <Stat size="sm">
                <StatLabel color="white">Completion</StatLabel>
                <StatNumber color="white">
                  {stats.completionRate.toFixed(0)}%
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Fight Results */}
        {event.fights && event.fights.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md" color="white">Fight Results</Heading>
            </CardHeader>
            <CardBody>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.300">Fight</Th>
                      <Th color="gray.300">Fighters</Th>
                      <Th color="gray.300">Weight Class</Th>
                      <Th color="gray.300">Status</Th>
                      <Th color="gray.300">Winner</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {event.fights.map((fight, index) => (
                      <Tr key={fight.id}>
                        <Td color="gray.200">Fight {index + 1}</Td>
                        <Td color="white">
                          {fight.fighter1?.name || 'Fighter 1'} vs {fight.fighter2?.name || 'Fighter 2'}
                        </Td>
                        <Td color="gray.200">{fight.weightClass}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              fight.status === 'completed' ? 'green' :
                                fight.status === 'active' ? 'blue' : 'gray'
                            }
                            size="sm"
                          >
                            {fight.status}
                          </Badge>
                        </Td>
                        <Td color="white">
                          {fight.status === 'completed' ? (
                            fight.winner ? (
                              <Text fontWeight="medium">
                                {fight.winner === fight.fighter1?.id
                                  ? fight.fighter1?.name
                                  : fight.fighter2?.name
                                }
                              </Text>
                            ) : (
                              <Badge colorScheme="yellow" size="sm">Draw</Badge>
                            )
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        )}
      </VStack>
    );
  };

  if (loading.events) {
    return <LoadingSpinner message="Loading event history..." />;
  }

  return (
    <Box maxW="7xl" mx="auto" p={6}>
      {/* Header */}
      <VStack spacing={6} mb={8}>
        <Heading size="xl" textAlign="center" color="white">
          Event History
        </Heading>

        {/* Search and Filters */}
        <Card w="full">
          <CardBody>
            <VStack spacing={4}>
              <HStack w="full" spacing={4}>
                <InputGroup flex={2}>
                  <InputLeftElement>
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search events by name or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>

                <Select
                  placeholder="All Years"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  flex={1}
                >
                  {getAvailableYears().map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>

                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  flex={1}
                >
                  <option value="">All Events</option>
                  <option value="completed">Completed</option>
                  <option value="active">Active</option>
                </Select>
              </HStack>

              <HStack justify="space-between" w="full">
                <Text fontSize="sm" color="gray.400">
                  Showing {filteredEvents.length} of {getAllEvents().length} events
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Events Grid */}
      {filteredEvents.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredEvents.map(event => (
            <EventCard key={event.id} event={event} />
          ))}
        </SimpleGrid>
      ) : (
        <Card>
          <CardBody textAlign="center" py={10}>
            <VStack spacing={4}>
              <Icon as={FiCalendar} boxSize={12} color="gray.400" />
              <VStack spacing={2}>
                <Text color="gray.400" fontSize="lg">
                  No events found
                </Text>
                <Text color="gray.500" fontSize="sm">
                  {searchTerm || yearFilter || statusFilter
                    ? 'Try adjusting your search criteria'
                    : 'No events have been created yet'
                  }
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Event Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>Event Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <EventDetailsModal event={selectedEvent} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EventHistory;
